import { useState } from "react";
import { toast } from "sonner";
import type { HotspotBy } from "@iamu/shared";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

interface HotspotEpicDialogProps {
  projectId: string;
  hotspotKey: string;
  by: HotspotBy;
  label: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SKIP_LABEL: Record<string, string> = {
  dismissed: "dismissed in iamu",
  not_problem: "strengths",
  verified: "verified fixed",
};

export function HotspotEpicDialog({
  projectId,
  hotspotKey,
  by,
  label,
  open,
  onOpenChange,
}: HotspotEpicDialogProps) {
  const [title, setTitle] = useState(`[Hotspot] ${label}`);
  const [createSubIssues, setCreateSubIssues] = useState(true);
  const [queuedSubIssues, setQueuedSubIssues] = useState(false);

  const utils = trpc.useUtils();
  const epicQuery = trpc.workflow.hotspotEpic.useQuery(
    { projectId, hotspotKey },
    { enabled: open },
  );

  const planQuery = trpc.workflow.bulkPreview.useQuery(
    {
      projectId,
      hotspotKey,
      hotspotBy: by,
      parentLinkId: epicQuery.data?.link?.id,
    },
    {
      enabled: open,
      refetchInterval: (q) =>
        queuedSubIssues &&
        q.state.data?.rows.some(
          (r) => r.status === "pending" || r.status === "create",
        )
          ? 3000
          : false,
    },
  );

  const createEpicMutation = trpc.workflow.createHotspotEpic.useMutation({
    onSuccess: (res) => {
      toast.success(
        res.queuedSubIssues > 0
          ? `Created hotspot epic ${res.link.displayKey ?? ""} and queued ${res.queuedSubIssues} sub-issues`
          : `Created hotspot epic ${res.link.displayKey ?? ""}`,
      );
      utils.workflow.hotspotEpic.invalidate();
      utils.workflow.bulkPreview.invalidate();
      if (res.queuedSubIssues > 0) {
        setQueuedSubIssues(true);
      } else {
        onOpenChange(false);
      }
    },
    onError: (err) => toastTrpcError(err, "Failed to create hotspot epic"),
  });

  const createSubIssuesMutation = trpc.workflow.bulkCreate.useMutation({
    onSuccess: (res) => {
      setQueuedSubIssues(true);
      toast.success(
        res.queued === 1
          ? "Creating 1 sub-issue in the background"
          : `Creating ${res.queued} sub-issues in the background`,
      );
      utils.workflow.bulkPreview.invalidate();
    },
    onError: (err) => toastTrpcError(err, "Failed to create sub-issues"),
  });

  const existingLink = epicQuery.data?.link;
  const rows = planQuery.data?.rows ?? [];
  const toCreate = rows.filter((r) => r.status === "create");
  const linked = rows.filter((r) => r.status === "linked");
  const pending = rows.filter((r) => r.status === "pending");
  const skipped = rows.filter((r) => r.status === "skip");
  const skipCounts = skipped.reduce<Record<string, number>>((acc, r) => {
    const k = r.skipReason ?? "dismissed";
    acc[k] = (acc[k] ?? 0) + 1;
    return acc;
  }, {});

  const blocked = !planQuery.data?.configured
    ? "GitHub isn't connected on this server."
    : !planQuery.data?.target
      ? "Link a GitHub repository to this project to create issues."
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {existingLink ? "Hotspot epic" : "Create hotspot epic"}
          </DialogTitle>
          <DialogDescription>
            {existingLink
              ? `Epic issue tracking the ${label} area on ${planQuery.data?.target ?? "GitHub"}.`
              : `Create a parent issue on ${planQuery.data?.target ?? "GitHub"} with a checklist of open problems.`}
          </DialogDescription>
        </DialogHeader>

        {epicQuery.isLoading || planQuery.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <div className="space-y-4 text-sm">
            {existingLink ? (
              <div className="rounded-md border p-3 space-y-1 bg-muted/40">
                <p className="font-semibold text-foreground">
                  Linked epic:{" "}
                  {existingLink.url ? (
                    <a
                      href={existingLink.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-4"
                    >
                      {existingLink.displayKey ?? "Epic"}: {existingLink.title}
                    </a>
                  ) : (
                    <span>
                      {existingLink.displayKey ?? "Epic"}: {existingLink.title}
                    </span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  Status: {existingLink.rawState ?? existingLink.category} ·
                  Closing the epic will not close or alter individual findings.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="epic-title">Issue title</Label>
                <Input
                  id="epic-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="[Hotspot] Area name"
                />
              </div>
            )}

            <div className="space-y-2">
              <h4 className="font-medium text-foreground">
                {existingLink ? "Member sub-issues" : "Area problems breakdown"}
              </h4>
              <ul className="space-y-1 text-sm">
                <li>
                  <span className="font-medium tabular-nums">
                    {toCreate.length}
                  </span>{" "}
                  {existingLink
                    ? "problems need an issue"
                    : "problems ready to ticket"}
                </li>
                {pending.length > 0 && (
                  <li>
                    <span className="font-medium tabular-nums">
                      {pending.length}
                    </span>{" "}
                    being created now
                  </li>
                )}
                {linked.length > 0 && (
                  <li>
                    <span className="font-medium tabular-nums">
                      {linked.length}
                    </span>{" "}
                    already have an issue:{" "}
                    <span className="text-muted-foreground">
                      {linked
                        .slice(0, 5)
                        .map((r) => r.displayKey)
                        .join(", ")}
                      {linked.length > 5 ? ", …" : ""}
                    </span>
                  </li>
                )}
                {Object.entries(skipCounts).map(([k, n]) => (
                  <li key={k}>
                    <span className="font-medium tabular-nums">{n}</span>{" "}
                    skipped: {SKIP_LABEL[k] ?? k}
                  </li>
                ))}
              </ul>
            </div>

            {!existingLink && toCreate.length > 0 && (
              <div className="flex items-center space-x-2 pt-2 border-t">
                <Checkbox
                  id="sub-issues-checkbox"
                  checked={createSubIssues}
                  onCheckedChange={(checked) =>
                    setCreateSubIssues(Boolean(checked))
                  }
                />
                <Label
                  htmlFor="sub-issues-checkbox"
                  className="cursor-pointer text-sm font-normal"
                >
                  Also create sub-issues for the {toCreate.length} open problems
                  (linked to this epic)
                </Label>
              </div>
            )}

            {blocked && <p className="text-muted-foreground">{blocked}</p>}
            {queuedSubIssues &&
              toCreate.length === 0 &&
              pending.length === 0 && (
                <p role="status">All sub-issues are created.</p>
              )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>

          {existingLink ? (
            <Button
              onClick={() =>
                createSubIssuesMutation.mutate({
                  projectId,
                  hotspotKey,
                  hotspotBy: by,
                  parentLinkId: existingLink.id,
                })
              }
              disabled={
                !!blocked ||
                toCreate.length === 0 ||
                createSubIssuesMutation.isPending ||
                queuedSubIssues
              }
            >
              {toCreate.length === 0
                ? "All sub-issues created"
                : toCreate.length === 1
                  ? "Create 1 sub-issue"
                  : `Create ${toCreate.length} sub-issues`}
            </Button>
          ) : (
            <Button
              onClick={() =>
                createEpicMutation.mutate({
                  projectId,
                  hotspotKey,
                  by,
                  title,
                  createSubIssues,
                })
              }
              disabled={!!blocked || createEpicMutation.isPending}
            >
              {createEpicMutation.isPending
                ? "Creating…"
                : createSubIssues && toCreate.length > 0
                  ? `Create epic & ${toCreate.length} sub-issues`
                  : "Create epic issue"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
